import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import WordsList, { WordItem } from '../components/WordsList';
import AddWordModal from '../components/AddWordModal';
import WordCard from '../components/WordCard';
import EditCategoryModal from '../components/EditCategoryModal';
import '../styles/DictionaryCategory.scss';
import { fetchCategoryWords, WordWithStatus, Word, addWordToCategory, ApiResponse, fetchData, WordStatus } from '../utils/api';
import { useTheme } from '../hooks/useTheme';
import alreadyLearnedIconLight from '../assets/light/alreadyLearnedIcon.svg';
import learningIconLight from '../assets/light/learningIcon.svg';
import blackListIconLight from '../assets/light/blackListIcon.svg';
import alreadyLearnedIconDark from '../assets/dark/alreadyLearnedIcon.svg';
import learningIconDark from '../assets/dark/learningIcon.svg';
import blackListIconDark from '../assets/dark/blackListIcon.svg';
import editIconLight from '../assets/light/EditIcon.svg';
import editIconDark from '../assets/dark/EditIcon.svg';
import addIconLight from '../assets/light/addIcon.svg';
import addIconDark from '../assets/dark/addIcon.svg';
import backIconLight from '../assets/light/backIcon.svg';
import backIconDark from '../assets/dark/backIcon.svg';
import logger from '../utils/logger';

interface CategoryInfo {
  icon?: string;
  emoji?: string;
  name: string;
}
const DictionaryCategory = () => {
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emojiFromUrl = queryParams.get('emoji') || '📝';
  const categoryId = params.categoryId || '';
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [words, setWords] = useState<WordItem[]>([]);
  const [categoryInfo, setCategoryInfo] = useState<CategoryInfo>({ 
    emoji: emojiFromUrl, 
    name: categoryId 
  });
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState<Word | null>(null);
  const isPredefinedCategory = ['known', 'learning', 'blacklist'].includes(categoryId);
  const contentRef = useRef<HTMLDivElement>(null);
  
  logger.log('Параметры URL:', params);
  logger.log('categoryId:', categoryId);
  const loadCategoryWords = async () => {
    logger.log('Начинаем загрузку данных для categoryId:', categoryId);
    setIsLoading(true);
    setError(null);
    try {
      const predefinedCategories: Record<string, { name: string, status: WordStatus, lightIcon: string, darkIcon: string }> = {
        'known': { 
          name: "Знаю", 
          status: "LEARNED",
          lightIcon: alreadyLearnedIconLight,
          darkIcon: alreadyLearnedIconDark
        },
        'learning': { 
          name: "Учу", 
          status: "IN_PROGRESS",
          lightIcon: learningIconLight,
          darkIcon: learningIconDark
        },
        'blacklist': { 
          name: "Чёрный список", 
          status: "BLACK_LIST",
          lightIcon: blackListIconLight,
          darkIcon: blackListIconDark
        }
      };
      if (predefinedCategories[categoryId]) {
        setCategoryInfo({
          icon: theme === 'light' ? predefinedCategories[categoryId].lightIcon : predefinedCategories[categoryId].darkIcon,
          name: predefinedCategories[categoryId].name
        });
        const status = predefinedCategories[categoryId].status;
        logger.log(`Загрузка слов со статусом: ${status}`);
        const response = await fetchData<WordWithStatus[]>(`/dictionary/words?status=${status}`);
        if (response.success && response.data) {
          logger.log(`Получены слова со статусом ${status}:`, response.data);
          const mappedWords = response.data.map((item): WordItem => ({
            characters: item.word.word,
            pinyin: item.word.pinyin,
            translation: item.word.translation,
            priority: item.word.priority,
            example: item.word.example,
            originalData: item,
            isLearned: item.status === 'LEARNED'
          }));
          const sortedWords = [...mappedWords].sort((a, b) => {
            if (a.isLearned && !b.isLearned) return 1;
            if (!a.isLearned && b.isLearned) return -1;
            return 0;
          });
          setWords(sortedWords);
        } else {
          setError(response.error || `Не удалось загрузить слова со статусом ${status}`);
          logger.error(`Ошибка при загрузке слов со статусом ${status}:`, response.error);
        }
        setIsLoading(false);
        return;
      }
      const categoryName = categoryId;
      const response = await fetchCategoryWords(categoryName);
      if (response.success && response.data) {
        logger.log('Получены слова по категории:', response.data);
        const mappedWords = response.data.map((item): WordItem => ({
          characters: item.word.word,
          pinyin: item.word.pinyin,
          translation: item.word.translation,
          priority: item.word.priority,
          example: item.word.example,
          originalData: item,
          isLearned: categoryId === 'known' || (item.status && item.status === 'LEARNED')
        }));
        const sortedWords = [...mappedWords].sort((a, b) => {
          if (a.isLearned && !b.isLearned) return 1;
          if (!a.isLearned && b.isLearned) return -1;
          return 0;
        });
        setWords(sortedWords);
        if (response.data.length > 0 && response.data[0].categories) {
          const category = response.data[0].categories.find(cat => cat.name === categoryName);
          if (category) {
            setCategoryInfo({
              emoji: category.emoji || emojiFromUrl,
              name: category.name
            });
          } else {
            setCategoryInfo({
              emoji: emojiFromUrl,
              name: categoryName
            });
          }
        } else {
          setCategoryInfo({
            emoji: emojiFromUrl,
            name: categoryName
          });
        }
      } else {
        setError(response.error || 'Не удалось загрузить слова для этой категории');
        logger.error('Ошибка при загрузке слов категории:', response.error);
      }
    } catch (error) {
      // Не отображаем ошибку пользователю
    } finally {
      setIsLoading(false);
      logger.log('Загрузка данных завершена');
    }
  };
  useEffect(() => {
    logger.log('DictionaryCategory монтирован');
    if (!categoryId) {
      logger.error('categoryId отсутствует!');
      setError('Не указан идентификатор категории');
      setIsLoading(false);
      return;
    }
    loadCategoryWords();
  }, [categoryId]);
  logger.log('Состояние компонента:', { isLoading, words, categoryInfo, error });
  const handleBackClick = () => {
    navigate('/dictionary');
  };
  const handleWordClick = (word: WordItem) => {
    logger.log('Выбрано слово:', word);
    const wordData: Word = {
      word: word.characters,
      pinyin: word.pinyin || '',
      translation: word.translation || '',
      priority: word.priority || 0,
      example: word.example
    };
    setSelectedWord(wordData);
  };
  const closeWordCard = () => {
    setSelectedWord(null);
  };
  const openAddModal = () => {
    logger.log('Открываем модальное окно добавления слова для категории:', categoryId);
    setIsAddModalOpen(true);
  };
  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };
  const openEditCategoryModal = () => {
    logger.log('Открываем модальное окно редактирования категории:', categoryId);
    setIsEditCategoryModalOpen(true);
  };
  const closeEditCategoryModal = () => {
    setIsEditCategoryModalOpen(false);
  };
  const handleWordAdded = () => {
    logger.log('Слово добавлено, обновляем список слов...');
    loadCategoryWords();
  };
  const handleCategoryUpdated = () => {
    logger.log('Категория обновлена, обновляем данные...');
    loadCategoryWords();
  };
  const handleStatusChange = (status: WordStatus) => {
    logger.log(`Статус слова изменен на: ${status}`);
    setSelectedWord(null);
    loadCategoryWords(); // Перезагружаем список слов после изменения статуса
  };
  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  return (
    <div className="dictionary-category-page">
      <div className="category-header">
        <button className="back-button" onClick={handleBackClick}>
          <img 
            src={theme === 'light' ? backIconLight : backIconDark} 
            alt="Назад" 
            width="36" 
            height="36" 
          />
        </button>
        <h1 className="category-title" onClick={scrollToTop}>
          {categoryInfo.icon ? (
            <img src={categoryInfo.icon} alt={categoryInfo.name} className="category-icon" />
          ) : null}
          {categoryInfo.emoji} {categoryInfo.name}
        </h1>
        <div className="header-buttons">
          {!isPredefinedCategory && (
            <>
              <button className="edit-category-button" onClick={openEditCategoryModal}>
                <img 
                  src={theme === 'light' ? editIconLight : editIconDark} 
                  alt="Редактировать" 
                  width="36" 
                  height="36" 
                />
              </button>
              <button className="add-word-button" onClick={openAddModal}>
                <img 
                  src={theme === 'light' ? addIconLight : addIconDark} 
                  alt="Добавить" 
                  width="36" 
                  height="36" 
                />
              </button>
            </>
          )}
          {isPredefinedCategory && (
            <div className="spacer"></div>
          )}
        </div>
      </div>
      
      {categoryId === 'blacklist' && (
        <div className="category-description">
          <p>Слова, добавленные в этот список, не будут отображаться при поиске и в качестве рекомендаций</p>
        </div>
      )}
      
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Загрузка...</div>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      ) : (
        <div className="category-content" ref={contentRef}>
          {words && words.length > 0 ? (
            <>
              <p className="word-count">Найдено слов: {words.length}</p>
              <WordsList 
                words={words} 
                className="results-list"
                itemClassName="result-item"
                onWordClick={handleWordClick}
                learnedIndicator={true}
              />
            </>
          ) : (
            <div className="empty-state">
              <p>В этой категории пока нет слов</p>
            </div>
          )}
        </div>
      )}
      <AddWordModal 
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        categoryName={categoryId}
        onWordAdded={handleWordAdded}
      />
      {selectedWord && (
        <WordCard 
          word={selectedWord}
          onClose={closeWordCard}
          onAddToCategory={() => {
            addWordToCategory(selectedWord, categoryId)
              .then((response: ApiResponse<any>) => {
                if (response.success) {
                  logger.log('Слово успешно добавлено в категорию из карточки');
                  closeWordCard();
                  setTimeout(() => {
                    loadCategoryWords(); 
                  }, 300);
                } else {
                  logger.error('Ошибка при добавлении слова из карточки:', response.error);
                }
              })
              .catch((err: Error) => {
                logger.error('Исключение при добавлении слова из карточки:', err);
              });
          }}
          categoryName={categoryId}
          isInCategory={true}
          showStatusButtons={true}
          onStatusChange={handleStatusChange}
        />
      )}
      <EditCategoryModal 
        isOpen={isEditCategoryModalOpen}
        onClose={closeEditCategoryModal}
        onCategoryUpdated={handleCategoryUpdated}
        categoryName={categoryInfo.name}
        categoryEmoji={categoryInfo.emoji || ''}
      />
    </div>
  );
};
export default DictionaryCategory; 